<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Error from '../../js/model/Error.js';
import KgrAvatarFormRow from '../../components/members/AvatarFormRow.vue';
import KgrCheckbox from '../../components/common/form/checkbox/Checkbox.vue';
import KgrCountriesSelect from '../../components/common/form/select/CountriesSelect.vue';
import KgrDatePicker from '../../components/common/form/picker/DatePicker.vue';
import KgrFormRow from '../../components/common/form/FormRow.vue';
import KgrFormScaffold from '../../components/common/form/FormScaffold.vue';
import KgrGenderSelect from '../../components/common/form/select/GenderSelect.vue';
import KgrLoading from '../../components/common/Loading.vue';
import KgrRecordLinkBanner from '@/components/domain/record-link/RecordLinkBanner.vue';
import KgrTitlesSelect from '../../components/members/AcademicTitleSelect.vue';
import type Member from '../../js/dto/Member';
import MemberRepository from '../../js/repository/MemberRepository';
import { useDialog } from '@/plugins/dialog';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = defineProps<{
    overviewUrl: string
    memberId: number
    clubId: number
    isEditable: boolean
    isDeletable: boolean
}>();

const { dialog } = useDialog();
const { notify } = useNotifications();
const { i18n } = useI18n();
const loading = ref(true);
const pending = ref(false);
const pendingRemove = ref(false);
const error = ref(new Error());
const member = ref<Member | null>(null);

onMounted(() => {
    loadMember(true);
});

function loadMember(initialLoad: boolean) {
    loading.value = initialLoad;
    MemberRepository.find(props.memberId)
        .then((loadedMember: Member) => {
            member.value = loadedMember;
        })
        .catch(() => {
            notify.error('error.loadingData');
        })
        .finally(() => {
            loading.value = false;
        });
}

function redirectToOverview() {
    window.location.href = props.overviewUrl;
}

async function removeMember() {
    if (!member.value) {
        return;
    }

    if (!dialog) {
        return;
    }

    const confirmed = await dialog.confirm({
        title: i18n.t('confirmInformal'),
        message: i18n.t('member.delete.confirm', { member: member.value.fullName() }),
        confirmButtonLabel: i18n.t('Ok'),
    });

    if (!confirmed) {
        return;
    }

    pendingRemove.value = true;
    MemberRepository.remove(member.value.id)
        .then(() => {
            error.value = new Error();
            notify.success('Mitglied erfolgreich gelöscht', {}, { saveInSession: true });
            redirectToOverview();
        })
        .catch((err) => handleError(err))
        .finally(() => pendingRemove.value = false);
}

async function updateMember() {
    if (!props.isEditable || !member.value) {
        return;
    }

    pending.value = true;
    MemberRepository.updateProfile(member.value)
        .then(() => {
            error.value = new Error();
            notify.success('saveChanges.success');
            loadMember(false);
        })
        .catch((err) => handleError(err))
        .finally(() => pending.value = false);
}

function handleError(err: any) {
    error.value = err;
    notify.error(err.message ?? 'saveChanges.error');
}
</script>

<template>
    <kgr-form-scaffold
        :pending="pending || loading"
        :pending-delete="pendingRemove"
        :with-delete-button="isDeletable"
        edit-mode
        :with-primary-buttons="isEditable"
        @primary-btn-clicked="updateMember"
        @delete-btn-clicked="removeMember"
        @back-btn-clicked="redirectToOverview"
    >
        <kgr-loading v-if="loading" />
        <div
            v-else-if="member"
            class="animated fadeIn"
        >
            <kgr-record-link-banner
                v-if="member.recordLink"
                :member-id="memberId"
                :record-link="member.recordLink"
            />
            <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters kgr-grid--bigGutters">
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="i18n.t('member.academicTitle')"
                        :error="error"
                        field-name="academicTitle"
                    >
                        <kgr-titles-select
                            v-model="member.academicTitle"
                            :club-id="clubId"
                        />
                    </kgr-form-row>

                    <kgr-form-row
                        :label="i18n.t('member.firstName')"
                        :error="error"
                        field-name="firstName"
                        required
                    >
                        <input
                            v-model.trim="member.firstName"
                            type="text"
                            class="kgr-input"
                            @keyup.enter="updateMember"
                        />
                    </kgr-form-row>

                    <kgr-form-row
                        :label="i18n.t('member.lastName')"
                        :error="error"
                        field-name="lastName"
                        required
                    >
                        <input
                            v-model.trim="member.lastName"
                            type="text"
                            class="kgr-input"
                            @keyup.enter="updateMember"
                        />
                    </kgr-form-row>

                    <kgr-form-row
                        :label="i18n.t('member.dateOfBirth')"
                        :error="error"
                        field-name="dateOfBirth"
                        :tooltip="i18n.t('Format: TT.MM.JJJJ')"
                    >
                        <kgr-date-picker
                            v-model.trim="member.dateOfBirth"
                            @enter="updateMember"
                        />
                    </kgr-form-row>

                    <kgr-form-row
                        :error="error"
                        field-name="deceased"
                    >
                        <kgr-checkbox
                            :model-value="member.deceased"
                            :label="i18n.t('Verstorben')"
                            @update:model-value="(value) => member && (member.deceased = value)"
                        />
                    </kgr-form-row>

                    <kgr-form-row
                        :label="i18n.t('member.birthName')"
                        :error="error"
                        field-name="birthName"
                    >
                        <input
                            v-model.trim="member.birthName"
                            type="text"
                            class="kgr-input"
                            @keyup.enter="updateMember"
                        />
                    </kgr-form-row>

                    <kgr-form-row
                        :label="i18n.t('member.gender')"
                        :error="error"
                        field-name="gender"
                    >
                        <kgr-gender-select
                            v-model="member.gender"
                            as-input
                        />
                    </kgr-form-row>

                    <kgr-form-row
                        :label="i18n.t('member.placeOfBirth')"
                        :error="error"
                        field-name="placeOfBirth"
                    >
                        <input
                            v-model.trim="member.placeOfBirth"
                            type="text"
                            class="kgr-input"
                            @keyup.enter="updateMember"
                        />
                    </kgr-form-row>

                    <kgr-form-row
                        :label="i18n.t('member.nationality')"
                        :error="error"
                        field-name="nationality"
                    >
                        <kgr-countries-select v-model="member.nationality" />
                    </kgr-form-row>
                </div>

                <div class="kgr-grid__cell">
                    <kgr-avatar-form-row :member="member" />

                    <kgr-form-row
                        :label="i18n.t('member.comment', { member: member.firstName })"
                        :error="error"
                        field-name="comment"
                    >
                        <textarea
                            v-model.trim="member.comment"
                            class="kgr-input"
                            rows="5"
                        ></textarea>
                    </kgr-form-row>

                    <kgr-form-row
                        v-if="member.isBookkeepingCommentViewable"
                        :label="i18n.t('member.bookkeepingComment', { member: member.firstName })"
                        :error="error"
                        field-name="bookkeepingComment"
                    >
                        <textarea
                            v-model.trim="member.bookkeepingComment"
                            class="kgr-input"
                            rows="5"
                        ></textarea>
                    </kgr-form-row>
                </div>
            </div>
        </div>
    </kgr-form-scaffold>
</template>

<style scoped lang="scss" rel="stylesheet/scss"></style>
